import React, { useCallback, useContext, useState, useSyncExternalStore } from 'react';
import { GiftFill, X } from 'react-bootstrap-icons';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  TRADESTATION_AOP_URL,
  TRADESTATION_PROMO_CTA_LABEL,
  TRADESTATION_PROMO_EYEBROW,
  TRADESTATION_PROMO_HEADLINE_AFTER,
  TRADESTATION_PROMO_HEADLINE_AFTER_ONBOARDING,
  TRADESTATION_PROMO_HEADLINE_BEFORE,
  TRADESTATION_PROMO_HOME_SOFT_FOOTER_BODY,
  TRADESTATION_PROMO_TABLE_ROW_LINK_LABEL,
  TRADESTATION_PROMO_TABLE_ROW_PREFIX,
  tradestationBrokerLogoUrl,
  tradestationPromoOfferFootnote,
} from '../../constants/tradestationReferral';
import analyticsService from '../../services/analyticsService';
import { isBrokerLinked } from '../../utils/brokerConnection';

import './TradeStationReferralPromoBar.css';

const STORAGE_KEY = 'pt_ts_referral_promo_dismissed';
const DISMISS_SYNC_EVENT = 'pt_ts_referral_promo_dismiss_sync';

/** Opaque strip background — dark mode uses a purple tint; light mode avoids transparent dark overlays. */
function promoStripBackground(colors, isDark) {
  if (isDark) {
    return `linear-gradient(125deg, ${colors.bgSecondary} 0%, rgba(139, 92, 246, 0.12) 50%, rgba(15, 23, 42, 0.4) 100%)`;
  }
  return `linear-gradient(125deg, ${colors.bgCard} 0%, rgba(99, 102, 241, 0.06) 52%, ${colors.bgSecondary} 100%)`;
}

function promoStripOnboardingBackground(colors, isDark) {
  if (isDark) {
    return `linear-gradient(125deg, ${colors.bgSecondary} 0%, rgba(139, 92, 246, 0.22) 45%, rgba(91, 33, 182, 0.18) 100%)`;
  }
  return `linear-gradient(125deg, ${colors.bgCard} 0%, rgba(99, 102, 241, 0.08) 50%, ${colors.bgSecondary} 100%)`;
}

const PUBLIC_TOP_PROMO_PATHS = [];
/** Paths where supported-brokers section shows the TradeStation promo strip (below title). */
const SUPPORTED_BROKERS_SECTION_PROMO_PATHS = ['/home', '/landing', '/pricing', '/brokers'];

/** Paths where site footer shows the * footnote when the offer is eligible (includes `/landing` in-section promo). */
const PUBLIC_PROMO_FOOTNOTE_PATHS = ['/', '/home', '/landing', '/pricing', '/brokers'];

function subscribePromoDismissed(onStoreChange) {
  if (typeof window === 'undefined') return () => {};
  const handler = () => onStoreChange();
  window.addEventListener('storage', handler);
  window.addEventListener(DISMISS_SYNC_EVENT, handler);
  return () => {
    window.removeEventListener('storage', handler);
    window.removeEventListener(DISMISS_SYNC_EVENT, handler);
  };
}

function getPromoDismissedSnapshot() {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch (_) {
    return false;
  }
}

function usePromoDismissedFromStorage() {
  return useSyncExternalStore(subscribePromoDismissed, getPromoDismissedSnapshot, () => false);
}

function usePromoDismissCommit() {
  return useCallback(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch (_) {
      /* ignore */
    }
    window.dispatchEvent(new Event(DISMISS_SYNC_EVENT));
  }, []);
}

function tradeStationTopPromoVisible({ variant, pathname, userToken, brokerLinked, dismissed }) {
  if (dismissed || brokerLinked) return false;
  if (variant === 'publicLanding') {
    return PUBLIC_TOP_PROMO_PATHS.includes(pathname);
  }
  if (!userToken) return false;
  if (pathname === '/onboarding') return false;
  return true;
}

/**
 * True when the user should see the TradeStation AOP offer on a public page (not dismissed, not already linked).
 */
export function useTradeStationPublicOfferEligible() {
  const { user } = useContext(AuthContext);
  const dismissed = usePromoDismissedFromStorage();
  return !dismissed && !isBrokerLinked(user);
}

/** `/landing` only: offer strips in hero, final CTA, and supported-brokers subcaption suppression. */
export function useTradeStationLandingBrokersPromoVisible() {
  const location = useLocation();
  const eligible = useTradeStationPublicOfferEligible();
  return eligible && location.pathname === '/landing';
}

/** `/landing` and `/pricing`: section promo below supported-brokers title. */
export function useTradeStationSupportedBrokersSectionPromoVisible() {
  const location = useLocation();
  const eligible = useTradeStationPublicOfferEligible();
  return eligible && SUPPORTED_BROKERS_SECTION_PROMO_PATHS.includes(location.pathname);
}

/**
 * `/home` only: user has not linked TradeStation. Ignores global promo dismiss storage so embedded
 * home promos can use per-mount dismiss state (see {@link TradeStationReferralPromoHomeHeroSection}).
 */
export function useTradeStationHomeEmbeddedPromoEligible() {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  return location.pathname === '/home' && !isBrokerLinked(user);
}

/** `/home` only: used for broker subcaption suppression when home may show TradeStation promos. */
export function useTradeStationHomeHeroPromoVisible() {
  return useTradeStationHomeEmbeddedPromoEligible();
}

/** Footer * line: show on public routes whenever the offer is eligible (in-section promos on /landing and /home). */
export function useTradeStationPublicPromoFootnoteVisible() {
  const location = useLocation();
  const eligible = useTradeStationPublicOfferEligible();
  return eligible && PUBLIC_PROMO_FOOTNOTE_PATHS.includes(location.pathname);
}

/** Same visibility rules as {@link TradeStationReferralPromoBar} / {@link TradeStationReferralPromoFooter}. */
export function useTradeStationTopPromoVisible(variant = 'authenticated') {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const dismissed = usePromoDismissedFromStorage();
  return tradeStationTopPromoVisible({
    variant,
    pathname: location.pathname,
    userToken: user?.token,
    brokerLinked: isBrokerLinked(user),
    dismissed,
  });
}

/** Homepage /home footer: left-aligned * note, no promo code in copy; visibility matches embedded home promos. */
export function TradeStationHomeSoftPromoFooterNote() {
  const { colors } = useTheme();
  const show = useTradeStationHomeEmbeddedPromoEligible();
  if (!show) return null;
  return (
    <div className="footer-disclaimer footer-disclaimer--lead footer-ts-promo-home-soft-note">
      <p
        role="note"
        style={{
          fontSize: 11,
          color: colors.textMuted,
          lineHeight: 1.45,
          textAlign: 'left',
          margin: 0,
        }}
      >
        <span aria-hidden style={{ marginRight: 4 }}>
          *
        </span>
        {TRADESTATION_PROMO_HOME_SOFT_FOOTER_BODY}
      </p>
    </div>
  );
}

/** Asterisk disclaimer line — use in page footers, step cards, or chart overlay (not inside {@link TradeStationPromoTextBlock}). */
export function TradeStationPromoFootnoteLine({ colors, align = 'center', style }) {
  return (
    <p
      role="note"
      style={{
        fontSize: 11,
        color: colors.textMuted,
        lineHeight: 1.45,
        textAlign: align,
        margin: 0,
        ...style,
      }}
    >
      <span aria-hidden style={{ marginRight: 4 }}>
        *
      </span>
      {tradestationPromoOfferFootnote()}
    </p>
  );
}

/** Compact TradeStation row promo — inline link + *; full disclaimer lives in site footer. */
export function TradeStationBrokerSubcaption({
  analyticsSource = 'supported_brokers_block',
  className = 'landing-ts-supported-promo',
}) {
  return (
    <p className={`landing-ts-supported-promo-headline ${className}`.trim()}>
      {TRADESTATION_PROMO_TABLE_ROW_PREFIX}
      <a
        href={TRADESTATION_AOP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="landing-broker-subcaption-link"
        onClick={() =>
          analyticsService.trackTradingEvent('tradestation_aop_link_clicked', {
            source: analyticsSource,
          })
        }
      >
        {TRADESTATION_PROMO_TABLE_ROW_LINK_LABEL}
      </a>
      <sup className="landing-ts-supported-promo-asterisk" aria-hidden>
        *
      </sup>
    </p>
  );
}

function promoHeadlineAfter(variant) {
  return variant === 'onboarding'
    ? TRADESTATION_PROMO_HEADLINE_AFTER_ONBOARDING
    : TRADESTATION_PROMO_HEADLINE_AFTER;
}

/** Headline with inline TradeStation logo + superscript * (ties to {@link TradeStationPromoFootnoteLine}). */
export function TradeStationPromoHeadlineWithMarker({
  offerColor,
  forceWhiteText = false,
  centered = false,
  variant = 'default',
}) {
  const headlineAfter = promoHeadlineAfter(variant);
  const logoStyle = {
    height: '1.22em',
    width: 'auto',
    maxWidth: 'min(10rem, 48vw)',
    objectFit: 'contain',
    display: 'inline-block',
    verticalAlign: '-0.12em',
  };
  const headlineColor = forceWhiteText ? '#ffffff' : undefined;
  const asteriskColor = forceWhiteText ? 'rgba(255, 255, 255, 0.9)' : offerColor;
  return (
    <span
      className={[
        'pt-ts-promo-headline-mark',
        variant === 'onboarding' ? 'pt-ts-promo-headline-mark--onboarding' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        display: 'inline-flex',
        flexWrap: variant === 'onboarding' ? 'nowrap' : 'wrap',
        flexDirection: variant === 'onboarding' ? 'column' : undefined,
        alignItems: centered ? 'center' : 'flex-start',
        justifyContent: centered ? 'center' : undefined,
        columnGap: '0.14em',
        rowGap: variant === 'onboarding' ? '0.06em' : '0.42em',
        maxWidth: '100%',
        color: headlineColor,
      }}
    >
      <span
        className="pt-ts-headline-row1"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
          columnGap: '0.22em',
          maxWidth: '100%',
          minWidth: 0,
        }}
      >
        <span className="pt-ts-headline-before">{TRADESTATION_PROMO_HEADLINE_BEFORE}</span>
        <span
          className="pt-ts-headline-logo-wrap"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            lineHeight: 0,
            flexShrink: 1,
            minWidth: 0,
          }}
        >
          <img
            className="pt-ts-headline-logo"
            src={tradestationBrokerLogoUrl()}
            alt="TradeStation"
            style={{ ...logoStyle, maxWidth: '100%' }}
          />
        </span>
      </span>
      <span className="pt-ts-headline-row2">
        <span>{headlineAfter}</span>
        <sup style={{ fontSize: '0.55em', fontWeight: 800, color: asteriskColor, lineHeight: 0 }} aria-hidden>
          *
        </sup>
      </span>
    </span>
  );
}

/**
 * Page footer disclaimer when the top TradeStation promo strip is shown (Environment shell).
 * Public `/home` and `/landing` use {@link SiteFooter} for the same footnote so it sits with legal copy
 * above the fixed StickyCTA (z-index) instead of being covered.
 */
export function TradeStationReferralPromoFooter({ variant = 'authenticated' }) {
  const { colors } = useTheme();
  const visible = useTradeStationTopPromoVisible(variant);

  if (!visible) return null;

  return (
    <footer
      role="note"
      style={{
        flexShrink: 0,
        padding: '8px 16px 12px',
        borderTop: `1px solid ${colors.border}`,
        background: colors.bgSecondary,
      }}
    >
      <TradeStationPromoFootnoteLine colors={colors} align="center" />
    </footer>
  );
}

/**
 * Shared promo: optional eyebrow + headline; optional CTA link (onboarding-only — elsewhere the logo/AOP
 * entry points or page footer carry the action).
 */
export function TradeStationPromoTextBlock({
  colors,
  analyticsSource,
  userId,
  showEyebrow = true,
  showCtaLink = false,
  /** `onboarding` omits "link to PrecisionTrader" from headline copy. */
  variant = 'default',
  /** When true, gift + copy are centered as a group (top promo strip). */
  centerGroup = false,
  /** Override headline font size (px). Default 16; use a smaller value (e.g. 13) in narrow containers. */
  headlineFontSize = 16,
}) {
  const { isDark } = useTheme();
  const offerColor = colors.primaryLight || colors.primary || '#8B5CF6';
  const promoCopyColor = isDark ? '#ffffff' : colors.text;
  const promoEyebrowColor = isDark ? '#ffffff' : colors.textMuted;
  const headlineAfter = promoHeadlineAfter(variant);
  const onAopClick = useCallback(() => {
    analyticsService.trackTradingEvent('tradestation_aop_link_clicked', {
      source: analyticsSource,
      user_id: userId,
    });
  }, [analyticsSource, userId]);

  return (
    <div
      className="pt-ts-promo-text-row"
      style={{
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        ...(centerGroup ? { justifyContent: 'center', width: '100%' } : {}),
      }}
    >
      <span
        className="pt-ts-promo-gift"
        aria-hidden
        style={{
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 0,
          opacity: 0.92,
        }}
      >
        <GiftFill size={26} color={offerColor} />
      </span>
      <div
        className="pt-ts-promo-copy"
        style={{
          minWidth: 0,
          flex: centerGroup ? '0 1 auto' : '1 1 auto',
          ...(centerGroup ? { maxWidth: '100%', textAlign: 'center' } : {}),
        }}
      >
        {showEyebrow ? (
          <div
            className="pt-ts-promo-eyebrow"
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: promoEyebrowColor,
              marginBottom: 6,
              opacity: isDark ? 0.95 : 1,
              ...(centerGroup ? { textAlign: 'center' } : {}),
            }}
          >
            {TRADESTATION_PROMO_EYEBROW}
          </div>
        ) : null}
        <div
          className="pt-ts-promo-headline"
          style={{
            fontSize: headlineFontSize,
            fontWeight: 800,
            color: promoCopyColor,
            lineHeight: 1.45,
            letterSpacing: '-0.02em',
            ...(centerGroup ? { display: 'flex', justifyContent: 'center' } : {}),
          }}
        >
          <a
            href={TRADESTATION_AOP_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onAopClick}
            aria-label={`${TRADESTATION_PROMO_HEADLINE_BEFORE}TradeStation${headlineAfter} (opens in new tab)`}
            style={{
              color: 'inherit',
              textDecoration: 'none',
              cursor: 'pointer',
              display: 'inline-block',
              maxWidth: '100%',
            }}
          >
            <TradeStationPromoHeadlineWithMarker
              offerColor={offerColor}
              forceWhiteText={isDark}
              centered={centerGroup}
              variant={variant}
            />
          </a>
        </div>
        {showCtaLink ? (
          <p
            className="pt-ts-promo-cta-line"
            style={{
              margin: '6px 0 0',
              fontSize: 12.5,
              color: colors.textLight,
              lineHeight: 1.45,
              ...(centerGroup ? { textAlign: 'center' } : {}),
            }}
          >
            <a
              href={TRADESTATION_AOP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onAopClick}
              style={{
                color: colors.text,
                fontWeight: 500,
                textDecoration: 'underline',
                textDecorationColor: `${offerColor}99`,
                textUnderlineOffset: 3,
              }}
            >
              {TRADESTATION_PROMO_CTA_LABEL}
            </a>
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Onboarding-only: below connect title. No logo; dismiss matches global strip (same storage key).
 */
export function TradeStationOnboardingPromoStrip() {
  const { user } = useContext(AuthContext);
  const { colors, isDark } = useTheme();
  const dismissed = usePromoDismissedFromStorage();
  const commitDismiss = usePromoDismissCommit();

  const onDismiss = useCallback(() => {
    commitDismiss();
  }, [commitDismiss]);

  if (dismissed || isBrokerLinked(user)) {
    return null;
  }

  const accent = colors.primaryLight || colors.primary || '#8B5CF6';
  return (
    <div
      role="region"
      aria-label="TradeStation account offer"
      className="pt-ts-referral-strip pt-ts-referral-strip--onboarding"
      style={{
        borderRadius: 10,
        border: `1px solid ${colors.border}`,
        borderLeft: `4px solid ${accent}`,
        background: promoStripOnboardingBackground(colors, isDark),
        boxShadow: isDark ? `inset 0 1px 0 rgba(255,255,255,0.04)` : `inset 0 1px 0 rgba(255,255,255,0.65)`,
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
      }}
    >
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        <TradeStationPromoTextBlock
          colors={colors}
          analyticsSource="onboarding_promo_strip"
          userId={user?.id}
          variant="onboarding"
          showCtaLink
          headlineFontSize={14}
        />
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss promotion"
        style={{
          flex: '0 0 auto',
          border: 'none',
          background: 'transparent',
          color: colors.textMuted,
          padding: 6,
          cursor: 'pointer',
          borderRadius: 6,
          lineHeight: 0,
        }}
      >
        <X size={20} aria-hidden />
      </button>
    </div>
  );
}

/**
 * Shared dismissible strip for public TradeStation AOP.
 * @param {'layout-top' | 'brokers' | 'hero' | 'landing-overlay'} embed — layout-top: full-width header; brokers: supported-brokers card; hero: narrow in-body card; landing-overlay: full-width strip (landing below header / home over hero).
 * @param {'start' | 'center'} [heroJustify] — when embed is hero, horizontal placement of the max-width card.
 * @param {() => void} [onDismissOverride] — if set, dismiss button calls this instead of writing global storage (home hero/close).
 */
function TradeStationReferralPublicPromoStrip({
  embed = 'layout-top',
  analyticsSource,
  showEyebrow,
  heroJustify = 'start',
  onDismissOverride,
}) {
  const { user } = useContext(AuthContext);
  const { colors, isDark } = useTheme();
  const commitDismiss = usePromoDismissCommit();
  const onDismiss = useCallback(() => {
    if (typeof onDismissOverride === 'function') {
      onDismissOverride();
    } else {
      commitDismiss();
    }
  }, [commitDismiss, onDismissOverride]);

  const accent = colors.primaryLight || colors.primary || '#8B5CF6';

  const layoutTopStyle = {
    flexShrink: 0,
    zIndex: 1001,
    position: 'relative',
    borderBottom: `1px solid ${colors.border}`,
    boxShadow: `inset 0 3px 0 ${accent}33`,
    background: promoStripBackground(colors, isDark),
    padding: '12px 36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 0,
  };

  const brokersEmbeddedStyle = {
    flexShrink: 0,
    position: 'relative',
    width: '100%',
    marginBottom: '1.25rem',
    borderRadius: 12,
    border: `1px solid ${colors.border}`,
    boxShadow: `inset 0 3px 0 ${accent}33`,
    background: promoStripBackground(colors, isDark),
    padding: '12px 36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 0,
  };

  const heroEmbeddedStyle = {
    ...brokersEmbeddedStyle,
    width: 'fit-content',
    maxWidth: 'min(900px, 100%)',
    marginBottom: '1.75rem',
    boxSizing: 'border-box',
    ...(heroJustify === 'center'
      ? { marginLeft: 'auto', marginRight: 'auto' }
      : { marginLeft: 0, marginRight: 'auto' }),
  };

  /** Same visual as layout-top: edge-to-edge strip for `/landing` and `/home` overlay placements. */
  const landingOverlayStyle = {
    ...layoutTopStyle,
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    borderRadius: 0,
  };

  const containerStyle =
    embed === 'layout-top'
      ? layoutTopStyle
      : embed === 'landing-overlay'
        ? landingOverlayStyle
        : embed === 'hero'
          ? heroEmbeddedStyle
          : brokersEmbeddedStyle;

  const stripClass = `pt-ts-referral-strip pt-ts-referral-strip--${embed}`;

  return (
    <div role="region" aria-label="TradeStation account offer" className={stripClass} style={containerStyle}>
      <TradeStationPromoTextBlock
        colors={colors}
        analyticsSource={analyticsSource}
        userId={user?.id}
        showEyebrow={showEyebrow}
        centerGroup
      />
      <button
        type="button"
        className="pt-ts-referral-strip-dismiss"
        onClick={onDismiss}
        aria-label="Dismiss promotion"
        style={{
          position: 'absolute',
          right: 10,
          top: '50%',
          transform: 'translateY(-50%)',
          border: 'none',
          background: 'transparent',
          color: colors.textMuted,
          padding: 6,
          cursor: 'pointer',
          borderRadius: 6,
          lineHeight: 0,
        }}
      >
        <X size={20} aria-hidden />
      </button>
    </div>
  );
}

/** Supported-brokers section: promo below the section title. */
export function TradeStationReferralPromoInSupportedBrokersSection() {
  const location = useLocation();
  const visible = useTradeStationSupportedBrokersSectionPromoVisible();
  if (!visible) return null;
  const analyticsSource = location.pathname === '/pricing'
    ? 'public_promo_pricing_supported_brokers'
    : location.pathname === '/home'
      ? 'public_promo_home_supported_brokers'
      : 'public_promo_supported_brokers';
  return (
    <TradeStationReferralPublicPromoStrip
      embed="brokers"
      analyticsSource={analyticsSource}
      showEyebrow={false}
    />
  );
}

/** `/home`: full-width strip over the top of the hero (not in document flow). Dismiss is per visit/placement (not global storage). */
export function TradeStationReferralPromoHomeHeroSection() {
  const eligible = useTradeStationHomeEmbeddedPromoEligible();
  const [dismissed, setDismissed] = useState(false);
  if (!eligible || dismissed) return null;
  return (
    <TradeStationReferralPublicPromoStrip
      embed="landing-overlay"
      analyticsSource="public_promo_home_hero"
      showEyebrow={false}
      onDismissOverride={() => setDismissed(true)}
    />
  );
}

/** `/landing`: full-width strip below minimal header, over the hero. Per-visit dismiss (not global storage). */
export function TradeStationReferralPromoLandingBelowHeader() {
  const visible = useTradeStationLandingBrokersPromoVisible();
  const [dismissed, setDismissed] = useState(false);
  if (!visible || dismissed) return null;
  return (
    <TradeStationReferralPublicPromoStrip
      embed="landing-overlay"
      analyticsSource="public_promo_landing_below_header"
      showEyebrow={false}
      onDismissOverride={() => setDismissed(true)}
    />
  );
}

/** `/landing`: full-width strip above the final CTA (e.g. “Your process matters…”). Per-visit dismiss. */
export function TradeStationReferralPromoLandingCtaFinal() {
  const visible = useTradeStationLandingBrokersPromoVisible();
  const [dismissed, setDismissed] = useState(false);
  if (!visible || dismissed) return null;
  return (
    <TradeStationReferralPublicPromoStrip
      embed="landing-overlay"
      analyticsSource="public_promo_landing_cta_final"
      showEyebrow={false}
      onDismissOverride={() => setDismissed(true)}
    />
  );
}

/** `/home` close section: below final subtitle, above primary CTA. Independently dismissable from hero. */
export function TradeStationReferralPromoHomeCloseSection() {
  const eligible = useTradeStationHomeEmbeddedPromoEligible();
  const [dismissed, setDismissed] = useState(false);
  if (!eligible || dismissed) return null;
  return (
    <TradeStationReferralPublicPromoStrip
      embed="hero"
      heroJustify="center"
      analyticsSource="public_promo_home_close"
      showEyebrow={false}
      onDismissOverride={() => setDismissed(true)}
    />
  );
}

/**
 * Dismissible TradeStation AOP promo strip.
 *
 * @param {'authenticated'|'publicLanding'} [variant='authenticated'] — `authenticated`: Environment
 *   (logged in, no TS link). `publicLanding`: reserved for layout header when {@link PUBLIC_TOP_PROMO_PATHS}
 *   includes the pathname; `/landing` uses {@link TradeStationReferralPromoInSupportedBrokersSection} only;
 *   `/home` uses {@link TradeStationReferralPromoHomeHeroSection} over the hero (per-visit dismiss, not global storage).
 */
export default function TradeStationReferralPromoBar({ variant = 'authenticated' }) {
  const analyticsSource = variant === 'publicLanding' ? 'public_promo_bar' : 'env_promo_bar';

  const visible = useTradeStationTopPromoVisible(variant);

  if (!visible) return null;

  return (
    <TradeStationReferralPublicPromoStrip
      embed="layout-top"
      analyticsSource={analyticsSource}
      showEyebrow={variant !== 'publicLanding'}
    />
  );
}
